import { useCallback } from "react";
import { useParams } from "react-router";
import { admin, orders as ordersApi } from "../../api/client";
import { STUCK_AFTER_MS, TERMINAL_STATES } from "../../api/types";
import { TocaPage } from "../../components/Shell";
import { ErrorBox, JsonView, Pill, Spinner, StatePill } from "../../components/ui";
import { useAsync, usePolling } from "../../hooks/useAsync";
import { fmt, t } from "../../i18n";
import { ageSince, dateTimeShort, formatCNPJ, timeHMS } from "../../lib/format";
import { brl, rate, usd } from "../../lib/money";
import { countryName, railDestination, railLabel } from "../../lib/rail";
import { describeEvent } from "../Pedido";
import { OrderActions, exceptionReason, needsManualRefund } from "./shared";

export function TocaPedido() {
  const { id = "" } = useParams();
  const { data, error, loading, reload, setData } = useAsync((signal) => admin.order(id, signal), [id]);
  const refresh = useCallback(async () => {
    try {
      setData(await admin.order(id));
    } catch {
      /* próximo tick */
    }
  }, [id, setData]);
  usePolling(refresh, 3000, Boolean(data) && !TERMINAL_STATES.has(data!.order.state));

  if (loading && !data) return <TocaPage title={t.toca.ordersTitle} backTo="/toca/pedidos"><Spinner /></TocaPage>;
  if (error || !data) return <TocaPage title={t.toca.ordersTitle} backTo="/toca/pedidos"><ErrorBox error={error} onRetry={reload} /></TocaPage>;

  const o = data.order;

  const ledgerEntries = Array.isArray(data.ledger) ? data.ledger : (data.ledger?.entries ?? []);
  const ex = exceptionReason({ ...o, pixCharge: data.pixCharge, execution: data.execution });
  const paid = data.pixCharge?.paidCents;
  const isException = o.state === "under_review" || o.state === "failed" || Boolean(paid && BigInt(paid) !== BigInt(o.totalCents));
  const events = [...data.events].sort((a, b) => b.seq - a.seq);
  const notes = data.notes ?? [];
  const manualRefund = needsManualRefund(o);
  // on-ramp parado: Pix entrou no parceiro (`funded`) há mais de STUCK_AFTER_MS e a conversão não terminou
  const stuckSince = o.state === "awaiting_funds" && data.pixCharge?.status === "funded" && data.pixCharge.paidAt && Date.now() - new Date(data.pixCharge.paidAt).getTime() > STUCK_AFTER_MS ? data.pixCharge.paidAt : null;

  return (
    <TocaPage title={fmt(t.toca.orderTitle, { id: o.id })} crumb={data.company?.legalName} backTo="/toca/pedidos">
      <div className="card" style={{ padding: "22px 24px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <div className="stack" style={{ gap: 4 }}>
          <div className="row" style={{ gap: 10 }}>
            <span className="mono" style={{ fontWeight: 600 }}>{o.id}</span>
            <StatePill state={o.state} />
            {isException && <Pill tone={ex.tone}>{ex.label}</Pill>}
          </div>
          <span className="muted small">{data.beneficiary?.legalName ?? "—"} · {data.company?.legalName ?? o.companyId ?? ""}</span>
          <div className="row" style={{ alignItems: "baseline", gap: 14 }}>
            <span className="bigval">{usd(o.usdCents)}</span>
            <span className="mono ink2">{brl(o.totalCents)} via Pix</span>
          </div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <OrderActions order={o} onDone={() => void refresh()} />
        </div>
      </div>

      {manualRefund && (
        <div className="card banner crit" role="alert" style={{ alignItems: "flex-start", flexDirection: "column", gap: 6 }}>
          <b>{t.toca.refundManualTitle}</b>
          <span>{fmt(t.toca.refundManualBody, { amount: brl(paid ?? o.totalCents) })}</span>
        </div>
      )}
      {stuckSince && (
        <div className="card banner warn" role="status" style={{ alignItems: "flex-start", flexDirection: "column", gap: 6 }}>
          <b>{t.toca.stuckTitle}</b>
          <span>{fmt(t.toca.stuckBanner, { age: ageSince(stuckSince) })}</span>
        </div>
      )}

      <div className="two narrow">
        <div className="stack">
          {isException && (
            <div className="card pad stack s8">
              <span className="lbl">{t.toca.whatHappened}</span>
              <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>{ex.detail}</p>
              {o.reviewReasons && o.reviewReasons.filter((r) => r !== "pix_amount_divergent").length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                  {o.reviewReasons.filter((r) => r !== "pix_amount_divergent").map((r) => <li key={r}>{t.reviewReasons[r] ?? r}</li>)}
                </ul>
              )}
              <div className="kv"><span className="k">{t.common.since}</span><span className="v">{dateTimeShort(o.createdAt)} · {ageSince(o.createdAt)}</span></div>
            </div>
          )}
          <div className="card pad">
            <h2 style={{ fontSize: 15, marginBottom: 14 }}>{t.toca.events}</h2>
            <div className="tl">
              {events.map((e) => {
                const d = describeEvent(e, data);
                return (
                  <div key={e.seq} className={`ev ${d.tone}`}>
                    <span className="t mono">{timeHMS(e.at)}</span>
                    <span className="d"><i aria-hidden="true" /></span>
                    <div className="b">
                      <b>{d.title} <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· {e.type} · {e.fromState ?? "∅"} → {e.toState}</span></b>
                      {d.desc && <span>{d.desc}</span>}
                      <small>{d.who}{e.actorId ? ` · ${e.actorId}` : ""}</small>
                      {e.payload && Object.keys(e.payload).length > 0 && <JsonView title="payload" value={e.payload} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="card pad stack s8">
            <h2 style={{ fontSize: 15 }}>{t.toca.partnerPayloads}</h2>
            {data.pixCharge && <JsonView title={`${t.toca.pixCharge} · ${data.pixCharge.txid}`} value={data.pixCharge} />}
            {data.execution && <JsonView title={`${t.toca.execution} · ${data.execution.status}`} value={data.execution} />}
            {data.partnerPayloads !== undefined && <JsonView title="partnerPayloads" value={data.partnerPayloads} />}
            {data.webhooks && data.webhooks.length > 0 && <JsonView title={`webhooks (${data.webhooks.length})`} value={data.webhooks} />}
            {!data.pixCharge && !data.execution && data.partnerPayloads === undefined && <span className="muted small">{t.common.empty}</span>}
          </div>
        </div>
        <div className="stack">
          <div className="card pad stack s8" style={{ fontSize: 13 }}>
            <span className="lbl">{t.toca.company}</span>
            <div className="kv"><span className="k">{t.settings.company}</span><span className="v">{data.company?.legalName ?? o.companyId ?? "—"}</span></div>
            {data.company?.cnpj && <div className="kv"><span className="k">CNPJ</span><span className="v mono">{formatCNPJ(data.company.cnpj)}</span></div>}
            <div className="kv"><span className="k">{t.suppliers.colName}</span><span className="v">{data.beneficiary?.legalName ?? "—"}</span></div>
            {data.beneficiary && (
              <>
                <div className="kv"><span className="k">{t.suppliers.colRail}</span><span className="v">{railLabel(data.beneficiary.rail)} · {countryName(data.beneficiary.country)}</span></div>
                <div className="kv"><span className="k">{t.suppliers.colDest}</span><span className="v mono wrap">{data.beneficiary.rail.kind === "stablecoin" ? data.beneficiary.rail.address : railDestination(data.beneficiary.rail)}</span></div>
              </>
            )}
            <div className="kv"><span className="k">{t.order.lockedRate}</span><span className="v mono">{rate(data.quote.rateE8)}</span></div>
            <div className="kv"><span className="k">{t.pix.totalPix}</span><span className="v mono">{brl(o.totalCents)}</span></div>
            {data.pixCharge?.paidCents && <div className="kv"><span className="k">Pix recebido</span><span className="v mono">{brl(data.pixCharge.paidCents)}</span></div>}
            {data.execution?.txHash && <div className="kv"><span className="k">{t.order.hash}</span><span className="v mono wrap">{data.execution.txHash}</span></div>}
            {data.execution?.failureCode && <div className="kv"><span className="k">failureCode</span><span className="v mono">{data.execution.failureCode}</span></div>}
            {o.reference && <div className="kv"><span className="k">{t.order.reference}</span><span className="v">{o.reference}</span></div>}
            <div className="row" style={{ marginTop: 6 }}>
              <a className="linkbtn" href={ordersApi.documentUrl(o.id, "invoice")} target="_blank" rel="noopener">{t.documents.invoice}</a>
              {o.state === "settled" && <a className="linkbtn" href={ordersApi.documentUrl(o.id, "receipt")} target="_blank" rel="noopener">{t.documents.receipt}</a>}
            </div>
          </div>
          <div className="card pad stack s8">
            <span className="lbl">{t.toca.ledger}</span>
            {ledgerEntries.length === 0 ? (
              <span className="muted small">{t.toca.ledgerEmpty}</span>
            ) : (
              <div className="tscroll" tabIndex={0}>
                <table style={{ fontSize: 12.5 }}>
                  <thead><tr><th>Entry</th><th>Conta</th><th className="r">Débito</th><th className="r">Crédito</th></tr></thead>
                  <tbody>
                    {ledgerEntries.flatMap((en) =>
                      en.lines.map((l, i) => (
                        <tr key={`${en.id}-${i}`}>
                          <td className="muted" style={{ whiteSpace: "nowrap" }}>{i === 0 ? `${en.kind} · ${timeHMS(en.postedAt)}` : ""}</td>
                          <td className="mono">{l.account}</td>
                          <td className="mono r">{BigInt(l.debitCents) ? brl(l.debitCents) : ""}</td>
                          <td className="mono r">{BigInt(l.creditCents) ? brl(l.creditCents) : ""}</td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="card pad stack s8">
            <span className="lbl">{t.toca.notes}</span>
            {notes.length === 0 ? (
              <span className="muted small">{t.toca.noNotes}</span>
            ) : (
              notes.map((n) => (
                <div key={n.id} style={{ fontSize: 13, lineHeight: 1.5, padding: "8px 0", borderBottom: "1px solid var(--surface-2)" }}>
                  <b>{n.authorName ?? n.authorId ?? t.actors.operator}</b> <span className="muted">· {dateTimeShort(n.createdAt)}</span>
                  <div>{n.body}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <p className="legal">{t.toca.queueFooter}</p>
    </TocaPage>
  );
}
